import { useEffect, useState, type FormEvent } from 'react';
import QRCode from 'qrcode';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { ShowOnceSecret } from '@/components/domain/ShowOnceSecret';
import { mfaEnable, mfaSetup, mfaVerify } from '@/api/endpoints/auth';
import { errorMessage } from '@/api/errors';
import type { MfaSetup } from '@/types/api';

interface MfaChallengeProps {
  challengeToken: string;
  /** True when the account has never enrolled and must do so to get in at all. */
  enrollmentRequired: boolean;
  onAuthenticated: (accessToken: string) => void;
  onCancel: () => void;
}

/**
 * The second half of signing in.
 *
 * Two shapes behind one component because they share the challenge token and the
 * code box: an account that has never enrolled sees the QR first, one that has just
 * types its current code.
 */
export function MfaChallenge(props: MfaChallengeProps) {
  return props.enrollmentRequired ? <Enroll {...props} /> : <Verify {...props} />;
}

function Verify({ challengeToken, onAuthenticated, onCancel }: MfaChallengeProps) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { access_token } = await mfaVerify(challengeToken, code);
      onAuthenticated(access_token);
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <p className="text-[13px] text-ink-secondary">
        Open Google Authenticator and enter the current six-digit code for AutoAware.
      </p>

      <TextField
        label="Authentication code"
        // A recovery code is not six digits, so no numeric input mode or maxLength.
        inputMode="text"
        autoComplete="one-time-code"
        autoFocus
        required
        value={code}
        onChange={(event) => setCode(event.target.value)}
        hint="Lost your phone? Enter one of your recovery codes instead."
      />

      {error && (
        <p className="rounded-[--radius-control] bg-bad-soft px-3 py-2 text-[13px] text-bad" role="alert">
          {error}
        </p>
      )}

      <Button type="submit" variant="primary" loading={busy} className="w-full">
        Verify
      </Button>
      <Button type="button" variant="ghost" onClick={onCancel} className="w-full">
        Back to sign in
      </Button>
    </form>
  );
}

function Enroll({ challengeToken, onAuthenticated, onCancel }: MfaChallengeProps) {
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    mfaSetup(challengeToken)
      .then(async (result) => {
        if (cancelled) return;
        setSetup(result);
        // Rendered in the browser: the otpauth URI carries the shared secret and
        // must not be handed to a third-party QR service.
        setQr(await QRCode.toDataURL(result.otpauth_uri, { margin: 1, width: 200 }));
      })
      .catch((caught) => !cancelled && setError(errorMessage(caught)));
    return () => {
      cancelled = true;
    };
  }, [challengeToken]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await mfaEnable(challengeToken, code);
      setCodes(result.recovery_codes);
      setToken(result.access_token);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  // Recovery codes exist in plaintext exactly once. Do not let anyone past this
  // without acknowledging it — a lost phone with no codes means a locked-out admin.
  if (codes && token) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-[15px] font-semibold text-ink">Save your recovery codes</h2>
          <p className="mt-1 text-[13px] text-ink-secondary">
            Each works once, and they are the only way in if you lose your phone. They
            are not shown again.
          </p>
        </div>

        <ShowOnceSecret
          rows={codes.map((value, index) => ({ label: `Code ${index + 1}`, value }))}
          filename="autoaware-recovery-codes"
        />

        <label className="flex items-start gap-2 text-[13px] text-ink-secondary">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.target.checked)}
            className="mt-0.5 size-4 rounded border-line-strong text-accent focus:ring-accent-ring"
          />
          I have saved these codes somewhere safe.
        </label>

        <Button
          variant="primary"
          className="w-full"
          disabled={!acknowledged}
          onClick={() => onAuthenticated(token)}
        >
          Continue
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <p className="text-[13px] text-ink-secondary">
        Administrator accounts need an authenticator app. Scan this in Google
        Authenticator, then enter the code it shows.
      </p>

      <div className="flex flex-col items-center gap-3 rounded-[--radius-control] border border-line bg-surface-sunken p-4">
        {qr ? (
          <img src={qr} alt="QR code for authenticator enrollment" className="size-[200px]" />
        ) : (
          <div className="size-[200px] animate-pulse rounded bg-line" />
        )}
        {setup && (
          <div className="text-center">
            <p className="text-[12px] text-ink-muted">Or enter this key by hand</p>
            <p className="mt-0.5 select-all font-mono text-[13px] text-ink" data-numeric>
              {setup.secret}
            </p>
          </div>
        )}
      </div>

      <TextField
        label="Code from the app"
        inputMode="numeric"
        autoComplete="one-time-code"
        required
        value={code}
        onChange={(event) => setCode(event.target.value)}
      />

      {error && (
        <p className="rounded-[--radius-control] bg-bad-soft px-3 py-2 text-[13px] text-bad" role="alert">
          {error}
        </p>
      )}

      <Button type="submit" variant="primary" loading={busy} disabled={!setup} className="w-full">
        Turn on two-factor
      </Button>
      <Button type="button" variant="ghost" onClick={onCancel} className="w-full">
        Back to sign in
      </Button>
    </form>
  );
}
